import banner from "../../../courses/module-1/img3.png";

const modulo3 = {
  courseId: "finanzas-personales",
  id: "modulo-3",
  title: "📘 MODULE 3: Purposeful Saving",
  estimatedDuration: "2 hours",
  format: "Reading + exercises + practical assignment",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objectives",
      title: "🎯 Module objectives",
      content: [
        "Understand why saving without a purpose can be demotivating.",
        "Identify different types of savings that adapt to your reality as a student.",
        "Design a personalized savings plan with clear, achievable, and motivating goals.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Main reading",
      title:
        "📖 Main reading: Saving just for the sake of saving? It does not always work",
      content: [
        {
          type: "paragraph",
          text: "Many times we are told “you have to save,” but no one explains what for or how. The problem with saving without a defined purpose is that:",
        },
        {
          type: "list",
          items: [
            "It is easy to break your savings because of any impulse or emotional expense.",
            "There is no clear motivation that encourages you to keep going.",
            "You may feel like you are saving for no reason and give up quickly.",
          ],
        },
        {
          type: "paragraph",
          text: "The real engine of saving is having a concrete, personal, and emotionally meaningful goal.",
        },
      ],
    },
    {
      id: "tipos",
      navLabel: "Types of savings",
      title: "🧩 Types of savings",
      content: [
        {
          type: "table",
          headers: ["Type", "What is it for?", "Real example"],
          rows: [
            [
              "Emergency fund",
              "For unexpected events: illness, accidents, losses",
              "Having $2,000 pesos saved for “whatever happens”",
            ],
            [
              "Goal-based savings",
              "For specific things, with a clear date and amount",
              "Paying for a passport, buying a laptop, going on a retreat",
            ],
            [
              "Long-term savings",
              "For larger projects or personal independence",
              "Moving out, starting a business, pursuing graduate studies",
            ],
          ],
        },
      ],
    },
    {
      id: "estrategias",
      navLabel: "Strategies",
      title: "🛠 Saving strategies for students",
      content: [
        {
          type: "list",
          items: [
            "The 10% method: save 10% of every income, no matter how small.",
            "Visual savings: print an image of your goal and place it somewhere you see every day.",
            "Envelope system, physical or digital: divide your money into categories.",
            "Smart rounding: if you spend $93, save the remaining $7 as “ghost change.”",
            "Useful apps: Fintonic, Presupuesto Familiar MX, Money Manager.",
            "Community saving: join savings groups with trusted friends or family members.",
          ],
        },
      ],
    },
    {
      id: "meta-smart",
      navLabel: "SMART goal",
      title: "🧠 How to define a SMART savings goal",
      content: [
        {
          type: "orderedList",
          items: [
            "Specific: What exactly do I want to achieve?",
            "Measurable: How much money do I need?",
            "Achievable: Is it realistic for my situation?",
            "Relevant: Why does this goal matter to me?",
            "Time-bound: How long do I want to take to achieve it?",
          ],
        },
        {
          type: "paragraph",
          text: "Example: “I want to save $1,500 to buy a new phone in 3 months, saving $125 every week.”",
        },
      ],
    },
    {
      id: "tabla-smart",
      navLabel: "SMART table",
      title: "📋 Interactive SMART goals table",
      content: [
        {
          type: "link",
          href: "https://docs.google.com/spreadsheets/d/1Dxejb_qtBMcCADWzIDMf7K-s0r0Oplr14FMMzXyHzss/edit?gid=0",
          text: "Access the SMART goals table here",
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Activity",
      title: "🧠 Reflection exercise",
      content: [
        "1. Think of something you want to achieve and answer in your notebook:",
        {
          type: "list",
          items: [
            "What do I want?",
            "How much does it cost?",
            "When do I need it?",
            "How much can I save per week or month?",
            "What expense can I reduce to achieve it?",
          ],
        },
        "2. Write your full SMART goal.",
        "3. Fill in the table with that information.",
      ],
    },
    {
      id: "lectura-complementaria",
      navLabel: "Complementary reading",
      title: "📚 Complementary reading",
      content: [
        {
          type: "link",
          href: "https://www.pequenocerdocapitalista.com",
          text: "How to set goals you actually complete – Pequeño Cerdo Capitalista Blog",
        },
      ],
    },
    {
      id: "piramide",
      navLabel: "Investment pyramid",
      title: "🔺 Extra: What is the investment pyramid?",
      content: [
        {
          type: "paragraph",
          text: "The investment pyramid is a visual model that shows how you should distribute your money when you start investing, beginning with the safest options and gradually moving toward riskier ones in a strategic way.",
        },
        {
          type: "table",
          headers: [
            "Level",
            "Type of investment",
            "Characteristics",
            "Common examples",
          ],
          rows: [
            [
              "Base",
              "Savings and security",
              "Low risk, high liquidity, protection",
              "Savings account, CETES, savings groups",
            ],
            [
              "Moderate growth",
              "Medium risk, medium-to-long-term return",
              "Investment funds, bank promissory notes",
              "",
            ],
            [
              "High return",
              "Higher risk, possible high gain",
              "Cryptocurrencies, stocks, startups",
              "",
            ],
          ],
        },
      ],
    },
    {
      id: "evaluacion",
      navLabel: "Quick assessment",
      title: "📝 Quick assessment",
      content: [
        "Assignment: Create your savings plan with a SMART goal",
        "📌 What should it include?",
        {
          type: "list",
          items: [
            "Your specific and motivating goal",
            "Total amount to save",
            "Estimated time",
            "Weekly or monthly plan to achieve it",
            "Savings method, such as piggy bank, app, bank, savings group, etc.",
          ],
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliography",
      title: "📚 Bibliography for further learning",
      content: [
        "Sofía Macías – “Pequeño Cerdo Capitalista: Inversiones”",
        "CONDUSEF – “Savings for young people”",
        "Ramsey Solutions – “The baby steps for purposeful saving”",
      ],
    },
    {
      id: "frase",
      navLabel: "Quote of the day",
      title: "🧠 Quote of the day",
      content: [
        "“Saving is not keeping what is left over, but choosing to keep what brings you closer to your dreams.”",
      ],
    },
  ],
};

export default modulo3;
